import {
  AnchoredPopup,
  CloseIcon,
  IconButton,
  LabelChip,
  useAnchoredPopupAnchor,
} from "@carbon6/c6-frontend-design";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type SyntheticEvent,
} from "react";
import { SessionStatusChip } from "@/components/status/SessionStatusChip";
import { sessionDetailedStatus, sessionStatusDetail } from "@/modules/training/public";
import {
  buildSessionCardDatasets,
  buildSessionCardFacts,
  type SessionCardFactKey,
} from "./sessionCardFacts";
import type { SubmitSessionTableRow } from "./SubmitSessionTableRow";
import styles from "./SessionNameCell.module.css";

// 삭제 예정 칩 색. 유예가 지나면 영구 삭제되는 위험을 상태 칩 "실패"와 같은 error 톤으로
// 알린다(사용자 확정). 상태 칩과 나란히 있어도 축이 다르므로 혼동보다 경고를 우선한다.
const DELETED_CHIP_COLOR = 0xdc2626;

// 여는 지연과 닫는 유예는 c6 툴팁과 같다. 표를 훑는 동안 카드가 행마다 뜨지 않게 머물러야 열고,
// 칸에서 카드로 넘어가는 틈에서는 닫지 않는다.
const OPEN_DELAY_MS = 250;
const CLOSE_GRACE_MS = 100;

type Timer = ReturnType<typeof setTimeout>;

type SessionNameCellProps = {
  row: SubmitSessionTableRow;
};

// 세션 목록의 `실험 / Training Session` 칸. 칸 어디에 머물러도 목록 행 값으로 채운 상세 카드를
// 띄운다. hover 마다 요청하지 않는다. 카드는 표에 없는 값(진행량·남은 시간·소요 시간·시작점·입력
// 구성)을 보여준다. 칸 클릭은 그대로 행으로 올라가 상세 창을 연다.
//
// 두 줄은 c6 `TruncatedText` 를 쓰지 않고 말줄임 CSS 만 가진다. `TruncatedText` 는 말줄임과 전문
// 복원 버블이 한 묶음이다. 여기서는 잘린 전문을 카드가 보여주므로 버블이 뜨면 같은 hover 에 둘이
// 겹친다.
export function SessionNameCell({ row }: SessionNameCellProps): ReactNode {
  const card = useHoverCard();

  return (
    <>
      <div
        className={styles.cell}
        onPointerMove={card.onTriggerPointerMove}
        onPointerLeave={card.onTriggerPointerLeave}
        onPointerDown={card.onTriggerPointerDown}
      >
        <span className={styles.experimentText}>{row.experimentLabel}</span>
        <span className={styles.nameLine}>
          {row.deleted ? <DeletedChip /> : null}
          <span className={styles.nameText}>{row.displayName}</span>
        </span>
      </div>
      <AnchoredPopup
        open={card.open}
        anchor={card.anchor}
        onClose={card.dismiss}
        placement="bottom-start"
        role="presentation"
        // hover 로 뜨는 읽기 전용 카드다. 모달 안에서도 focus 를 옮기거나 되돌리지 않는다.
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={false}
        returnFocus={false}
        width="22rem"
      >
        {/* 카드는 portal 이지만 React 이벤트는 컴포넌트 트리로 올라간다. 막지 않으면 카드 안
            클릭이 행 클릭이 되어 상세 창이 열린다. 래퍼는 조작 대상이 아니고 hover 경계와 전파
            차단만 맡으므로 role="presentation" 이다. */}
        <div
          className={styles.card}
          role="presentation"
          onPointerEnter={card.onCardPointerEnter}
          onPointerLeave={card.onCardPointerLeave}
          onPointerDown={stopRowEvent}
          onClick={stopRowEvent}
        >
          <AnchoredPopup.Body>
            <div className={styles.content}>
              <div className={styles.head}>
                <div className={styles.titleBlock}>
                  <span className={styles.cardTitle}>
                    {row.deleted ? <DeletedChip /> : null}
                    <span className={styles.cardName}>{row.displayName}</span>
                  </span>
                  <span className={styles.cardExperiment}>{row.experimentLabel}</span>
                </div>
                <span className={styles.status}>
                  <SessionStatusChip status={row.status} showIcon={false}>
                    {row.statusLabel}
                  </SessionStatusChip>
                </span>
                {/* 포인터를 카드 밖으로 빼지 않고도 닫는다. 영역을 벗어나면 닫히는 동작은 그대로다. */}
                <span className={styles.close}>
                  <IconButton
                    aria-label="닫기"
                    type="button"
                    variant="surface"
                    size="xs"
                    onClick={card.dismiss}
                  >
                    <CloseIcon />
                  </IconButton>
                </span>
              </div>
              <SessionFacts row={row} />
            </div>
          </AnchoredPopup.Body>
        </div>
      </AnchoredPopup>
    </>
  );
}

// 반폭에 두는 짧은 값. 한 줄에 둘씩 놓는다. 나머지는 전폭에서 줄을 바꾼다.
const HALF_WIDTH_FACTS: ReadonlySet<SessionCardFactKey> = new Set([
  "stage",
  "eta",
  "saved",
  "elapsed",
  "duration",
]);

// 키 칩과 굵은 값은 데이터셋 카드의 메타 목록과 같은 모양이다. 표의 다른 열에 이미 있는 값(생성·
// 멤버)은 싣지 않는다. 줄 구성은 상태에 따라 달라진다(`buildSessionCardFacts`).
function SessionFacts({ row }: SessionNameCellProps): ReactNode {
  const view = row.getView();
  // 경과 시간의 기준 시각. 카드는 열릴 때마다 새로 마운트되므로 열린 순간의 시각이다.
  const [openedAtMs] = useState(() => Date.now());
  const facts = buildSessionCardFacts(view, openedAtMs);
  const datasets = buildSessionCardDatasets(view);
  // 칩이 단계·진행률로 바뀌어 있으면 세부 사유가 가려진다. 칩이 이미 세부를 담고 있거나 세부가
  // 카테고리 이름과 같으면 줄을 두지 않는다.
  const detail =
    sessionDetailedStatus(row.status).label === row.statusLabel
      ? null
      : sessionStatusDetail(row.status);
  const note = row.hasNote ? view.note?.note.trim() : undefined;

  return (
    <dl className={styles.meta}>
      {facts.map((fact) => {
        // 반폭 값은 줄을 바꾸지 않고 자른다. 잘린 값은 title 로 읽는다.
        const half = HALF_WIDTH_FACTS.has(fact.key);
        return (
          <div
            key={fact.key}
            data-span={half ? "half" : undefined}
            title={half ? fact.value : undefined}
          >
            <dt>{fact.label}</dt>
            <dd>{fact.value}</dd>
          </div>
        );
      })}
      {datasets ? (
        <div>
          <dt>Dataset</dt>
          <dd className={styles.datasets}>
            {/* 같은 데이터셋을 비율만 바꿔 두 번 넣을 수 있어 항목이 이름으로 유일하지 않다.
                대표 순서(= 배열 위치)가 유일한 키다. */}
            {datasets.entries.map((entry, index) => (
              <span key={index} className={styles.datasetEntry}>
                <span>{entry.name}</span>
                <span className={styles.datasetUsage}>{entry.usage}</span>
              </span>
            ))}
            {datasets.extraCount > 0 ? (
              <span className={styles.datasetUsage}>외 {datasets.extraCount}건</span>
            ) : null}
          </dd>
        </div>
      ) : null}
      {detail ? (
        <div>
          <dt>상세</dt>
          <dd>{detail}</dd>
        </div>
      ) : null}
      {note ? (
        <div>
          <dt>노트</dt>
          <dd className={styles.note}>{note}</dd>
        </div>
      ) : null}
    </dl>
  );
}

function DeletedChip(): ReactNode {
  return (
    <span className={styles.deletedChip}>
      <LabelChip color={DELETED_CHIP_COLOR} size="xs" variant="filled">
        삭제 예정
      </LabelChip>
    </span>
  );
}

type HoverCard = {
  open: boolean;
  anchor: ReturnType<typeof useAnchoredPopupAnchor>["anchor"];
  dismiss: () => void;
  onTriggerPointerMove: (event: ReactPointerEvent<HTMLElement>) => void;
  onTriggerPointerLeave: () => void;
  onTriggerPointerDown: () => void;
  onCardPointerEnter: () => void;
  onCardPointerLeave: () => void;
};

// 칸 hover 로 여닫는 카드의 상태. AnchoredPopup 은 클릭으로 여는 표면이라 hover 수명은 여기서 정한다.
//
// - 여는 대기는 pointerenter 가 아니라 칸 안의 실제 pointermove 에서 시작한다. 휠 스크롤로 멈춘
//   포인터 아래 행만 바뀐 경우에는 pointermove 가 오지 않으므로 카드가 뜨지 않는다.
// - 포인터가 칸 위에 있는 채로 닫으면(상세 창을 여는 칸 클릭, 칸 위에서 Escape) 포인터가 칸을
//   떠날 때까지 다시 열지 않는다. 같은 칸에서 조금만 움직여도 방금 닫은 카드가 다시 뜨면 안 된다.
//   포인터가 카드 위에 있을 때 닫으면(닫기 버튼) 억제하지 않는다. 칸으로 돌아오는 것은 새 hover 다.
// - 여는 대기 중이거나 열린 동안 어디서든 스크롤하면 닫는다. 카드가 움직이는 행을 따라가지 않는다.
function useHoverCard(): HoverCard {
  const { anchor, open, setAnchor, close } = useAnchoredPopupAnchor();
  const [pending, setPending] = useState(false);
  const openTimer = useRef<Timer | null>(null);
  const closeTimer = useRef<Timer | null>(null);
  const overTrigger = useRef(false);
  const suppressed = useRef(false);

  const cancelOpen = useCallback(() => {
    if (openTimer.current !== null) {
      clearTimeout(openTimer.current);
      openTimer.current = null;
    }
    setPending(false);
  }, []);

  const cancelClose = useCallback(() => {
    if (closeTimer.current !== null) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const hide = useCallback(() => {
    cancelOpen();
    cancelClose();
    close();
  }, [cancelOpen, cancelClose, close]);

  const dismiss = useCallback(() => {
    suppressed.current = overTrigger.current;
    hide();
  }, [hide]);

  const onTriggerPointerDown = useCallback(() => {
    // 휠 스크롤 뒤 멈춘 포인터처럼 pointermove 없이 누르는 경우에도 칸 위다.
    overTrigger.current = true;
    dismiss();
  }, [dismiss]);

  const scheduleClose = useCallback(() => {
    cancelOpen();
    cancelClose();
    closeTimer.current = setTimeout(() => {
      closeTimer.current = null;
      close();
    }, CLOSE_GRACE_MS);
  }, [cancelOpen, cancelClose, close]);

  const onTriggerPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      overTrigger.current = true;
      // 터치에는 hover 가 없다. 스크롤·드래그 중의 pointermove 로 카드를 열지 않는다.
      if (event.pointerType === "touch" || suppressed.current) {
        return;
      }
      cancelClose();
      if (open || openTimer.current !== null) {
        return;
      }
      const el = event.currentTarget;
      setPending(true);
      openTimer.current = setTimeout(() => {
        openTimer.current = null;
        setPending(false);
        setAnchor({ type: "rect", rect: el.getBoundingClientRect(), el });
      }, OPEN_DELAY_MS);
    },
    [open, cancelClose, setAnchor],
  );

  const onTriggerPointerLeave = useCallback(() => {
    overTrigger.current = false;
    suppressed.current = false;
    scheduleClose();
  }, [scheduleClose]);

  useEffect(() => {
    if (!open && !pending) {
      return;
    }
    window.addEventListener("scroll", hide, true);
    return () => window.removeEventListener("scroll", hide, true);
  }, [open, pending, hide]);

  useEffect(
    () => () => {
      if (openTimer.current !== null) {
        clearTimeout(openTimer.current);
      }
      if (closeTimer.current !== null) {
        clearTimeout(closeTimer.current);
      }
    },
    [],
  );

  return {
    open,
    anchor,
    dismiss,
    onTriggerPointerMove,
    onTriggerPointerLeave,
    onTriggerPointerDown,
    onCardPointerEnter: cancelClose,
    onCardPointerLeave: scheduleClose,
  };
}

function stopRowEvent(event: SyntheticEvent): void {
  event.stopPropagation();
}
